import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { deleteItems, updateStateData } from "../../../Store/actions";
import store from "../../../Store/store";

// retailerSelection already imports this file, so load the check only when a save starts.
const retailerSelection = () => require("../../../api_calls/retailerSelection");

/** CAP Explorer light red — matches StyledLightRedInformationTypography / HtmlTooltipLightRed */
export const CAP_LIGHT_RED = "#F04B55";

const RETAILER_SCOPE_NOTICE_DISMISSED_KEY = "retailerScopeNoticeDismissed";

export const isRetailerScopeNoticeDismissed = () =>
  sessionStorage.getItem(RETAILER_SCOPE_NOTICE_DISMISSED_KEY) === "1";

export const dismissRetailerScopeNotice = () => {
  sessionStorage.setItem(RETAILER_SCOPE_NOTICE_DISMISSED_KEY, "1");
};

export const clearRetailerScopeNoticeDismissal = () => {
  sessionStorage.removeItem(RETAILER_SCOPE_NOTICE_DISMISSED_KEY);
};

export const PERSISTENT_TOP_CENTER_TOAST = {
  position: "top-center",
  autoClose: false,
};

export const BROWSER_COMPAT_TOAST_OPTIONS = {
  ...PERSISTENT_TOP_CENTER_TOAST,
  type: "info",
  theme: "colored",
};

export const RETAILER_SCOPE_TOAST_ID = "retailer-scope-workspace-notice";

export const RetailerScopeNotice = ({ retailerName }) => {
  const name = typeof retailerName === "string" && retailerName !== "" ? retailerName : "this retailer";

  return (
    <Box sx={{ color: "#fff", textAlign: "left" }}>
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.4 }}>
        Showing only entries that include {name}.
      </Typography>
      <Typography sx={{ color: "#fff", fontSize: "0.95rem", lineHeight: 1.4 }}>
        Dashboards only show data from this retailer's stores.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button
          onClick={() => {
            dismissRetailerScopeNotice();
            toast.dismiss(RETAILER_SCOPE_TOAST_ID);
          }}
          sx={{
            color: "#fff",
            backgroundColor: "#AF3241",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.85rem",
            lineHeight: 1.4,
            px: 1.5,
            py: 0.4,
            minWidth: 0,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#AF3241",
              boxShadow: "none",
            },
          }}
        >
          I understand
        </Button>
      </Box>
    </Box>
  );
};

export const RETAILER_SCOPE_TOAST_STYLE = {
  backgroundColor: CAP_LIGHT_RED,
  color: "#fff",
};

export const RETAILER_SCOPE_TOAST_PROGRESS_STYLE = {
  background: "rgba(255, 255, 255, 0.7)",
};

export const getRetailerScopeToastOptions = () => ({
  ...PERSISTENT_TOP_CENTER_TOAST,
  toastId: RETAILER_SCOPE_TOAST_ID,
  theme: "light",
  style: RETAILER_SCOPE_TOAST_STYLE,
  progressStyle: RETAILER_SCOPE_TOAST_PROGRESS_STYLE,
  closeButton: false,
  closeOnClick: false,
  draggable: false,
  hideProgressBar: true,
});

// Kept apart from the form fields. Those are cleared after a successful save.
export const OWNER_OUTSIDE_RETAILER_NOTICE_KEY = "owner_outside_retailer_notice";

const OWNER_OUTSIDE_RETAILER_TOAST_ID = "owner-outside-retailer-notice";

// Bottom line on the blue info toast. The browser-compatibility notice uses the same info style.
const OWNER_OUTSIDE_RETAILER_BOTTOM_LINE = "#1565C0";

const readStagedOwnerOutsideRetailerNotice = () =>
  store.getState().userSelections[OWNER_OUTSIDE_RETAILER_NOTICE_KEY];

export const clearStagedOwnerOutsideRetailerNotice = () => {
  if (typeof readStagedOwnerOutsideRetailerNotice() === "undefined") {
    return;
  }
  store.dispatch(deleteItems([OWNER_OUTSIDE_RETAILER_NOTICE_KEY]));
};

// Closes the box and drops the stored value. Used when the filter returns to All Retailers.
export const dismissOwnerOutsideRetailerNotice = () => {
  toast.dismiss(OWNER_OUTSIDE_RETAILER_TOAST_ID);
  clearStagedOwnerOutsideRetailerNotice();
};

// Call before the create request, while the owner is still on the form.
// isCreate false clears any earlier value so an update cannot show this notice.
export const stageOwnerOutsideRetailerNotice = (ownerCompanyId, isCreate) => {
  if (isCreate !== true) {
    clearStagedOwnerOutsideRetailerNotice();
    return;
  }

  const notice = retailerSelection().ownerOutsideSelectedRetailer(ownerCompanyId);
  if (notice == null) {
    clearStagedOwnerOutsideRetailerNotice();
    return;
  }

  store.dispatch(
    updateStateData({
      [OWNER_OUTSIDE_RETAILER_NOTICE_KEY]: notice,
    })
  );
};

// Call after the success toast. The form may already be reset; the copy comes from the staged value.
export const showStagedOwnerOutsideRetailerNotice = () => {
  const notice = readStagedOwnerOutsideRetailerNotice();
  if (notice == null || typeof notice.retailerName !== "string" || notice.retailerName === "") {
    return;
  }

  const message = 'Entry hidden by the current Retailer Filter. Select "All Retailers" to view it.';
  const options = {
    type: "info",
    theme: "colored",
    autoClose: false,
    position: "top-right",
    closeButton: true,
    closeOnClick: false,
    draggable: false,
    toastId: OWNER_OUTSIDE_RETAILER_TOAST_ID,
    hideProgressBar: false,
    progress: 1,
    progressStyle: { background: OWNER_OUTSIDE_RETAILER_BOTTOM_LINE },
    onClose: () => {
      clearStagedOwnerOutsideRetailerNotice();
    },
  };

  if (toast.isActive(OWNER_OUTSIDE_RETAILER_TOAST_ID) === true) {
    toast.update(OWNER_OUTSIDE_RETAILER_TOAST_ID, { render: message, ...options });
    return;
  }
  toast(message, options);
};
